import {
  useAddAdminMutation,
  useDeleteAdminMutation,
} from "@/generated/graphql_billing";
import { useCallBillingAddAdminMutation } from "@/components/hooks/useCallBillingAddAdminMutation";
import { useCallBillingDeleteAdminMutation } from "@/components/hooks/useCallBillingDeleteAdminMutation";
import { useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";

/**
 * BizSOLからSSOした際に最新のBizSOL権限に基づいて
 * Mikatano ID上での権限（管理者・一般）を更新する
 * 本関数では、上記に伴い、
 * 契約管理者 権限を更新する
 *
 * SSO完了後、現在のユーザーの権限が‥
 * 1) 管理者＆契約管理者である場合：何もしない
 * 2) 管理者＆契約管理者でない場合：契約管理者にする
 * 3) 一般＆契約管理者である場合：契約管理者の権限を剥奪する
 * 4) 一般＆契約管理者でない場合：何もしない
 *
 * [Note]
 * 2023.04現在 本処理は四国銀行のみで動作する
 * 京都信用金庫は、マスターユーザーのみSSOするという運用のため
 * Bizsol側の権限がマスターから変わることがない想定
 */
export function useUpdateContractAdminStatus() {
  // call custom hooks
  const [execAddAdminMutation] = useCallBillingAddAdminMutation();
  const [execDeleteAdminMutation] = useCallBillingDeleteAdminMutation();

  // define state for loading, error and result
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const [result, setResult] = useState<boolean | undefined>(undefined);

  // main process
  const actionUpdateContractAdminStatus = async (
    curUserSub: string,
    curUserAuthority: string,
    isCurUserContractAdmin: boolean
  ) => {
    // initialize
    setLoading(true);
    setError(undefined);
    setResult(undefined);

    // 現在の権限を取得
    const isAdmin = curUserAuthority === "admin";

    if (isAdmin) {
      // 管理者＆契約管理者でない場合：契約管理者にする
      const shouldChangeToContractAdmin = !isCurUserContractAdmin;
      // 契約管理者に変更する
      if (shouldChangeToContractAdmin) {
        const result = await execAddAdminMutation(curUserSub);
        setLoading(result.loading);
        setError(result.error);
        setResult(result.data?.addAdmin.result);
      } else {
        // 何もせず完了
        setLoading(false);
        setError(undefined);
        setResult(true);
      }
    } else {
      // 一般＆契約管理者である場合：契約管理者の権限を剥奪する
      const shouldRevokeContractAdmin = isCurUserContractAdmin;
      // 契約管理者の権限を剥奪する
      if (shouldRevokeContractAdmin) {
        const result = await execDeleteAdminMutation(curUserSub);
        setLoading(result.loading);
        setError(result.error);
        setResult(result.data?.deleteAdmin.result);
      } else {
        // 何もせず完了
        setLoading(false);
        setError(undefined);
        setResult(true);
      }
    }
  };

  return {
    loading,
    error,
    result,
    actionUpdateContractAdminStatus,
  };
}
